import type { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import type { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  type Features,
  Strategy,
} from '@wix/ambassador-bookings-v2-bookings-settings/types';
import {
  bookingsUoUFlowBookInitialIntentSrc16Evid33,
  bookingsUoUFlowSlotSelectionCompletedSrc16Evid34,
} from '@wix/bi-logger-bookings-data/v2';
import { bookingsContactInfoSaveSuccess } from '@wix/bi-logger-wixboost-ugc/v2';
import { navigateToBookingsFormPage } from '@wix/bookings-booking-navigation';
import { getTrackingInfoForCalendarNextButtonClick } from '@wix/bookings-calendar-catalog-viewer-analytics-adapter';
import { navigateToBookingsServicePage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import {
  BookingsQueryParams,
  CalendarSelections,
  getCurrentPath,
  isBookingFormInstalled as isBookingFormInstalledUtils,
  isTemplateMode,
  trackAnalytics,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import type { SelectedAddOn } from '@wix/bookings-services-preferences-modal/dist/types/types';
import {
  type AddonsInfo,
  type MultiAddonsInfo,
  ResourceSelectionStrategy,
} from '@wix/bookings-uou-types';
import {
  DO_NOT_CARE_STAFF,
  STAFF_RESOURCE_ID,
} from '../../../../constants/constants';
import { Preference } from '../../../../types/types';
import { PresetToWidgetNameMap } from '../../../../utils/bi/consts';
import {
  mapFlowTypeToBiProperties,
  mapStateToProperties,
} from '../../../../utils/bi/mappers';
import type { CalendarContext } from '../../../../utils/context/contextFactory';
import type { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { getDynamicPricingInfo } from '../../../../utils/dynamicPricing/dynamicPricing';
import { PanoramaTransactions } from '../../../../utils/panorama/consts';
import { isCalendarPage } from '../../../../utils/presets';
import { isBookingsEnabled } from '../../../../utils/serviceUtils/serviceUtils';
import {
  getStaffList,
  isSlotNoBookFlow,
} from '../../../../utils/slotAvailability/slotAvailability';
import type { CalendarState } from '../../controller';
import {
  DialogState,
  DialogType,
} from '../../ViewModel/dialogViewModel/dialogViewModel';
import { CalendarStatus } from '../../ViewModel/widgetViewModel/widgetViewModel';

export type NavigateToNextPage = ({
  selectedSlots,
  isWaitingListFlow,
}: {
  selectedSlots: TimeSlot[];
  isWaitingListFlow: boolean;
}) => Promise<void>;

export function createNavigateToNextPageAction(
  actionFactoryParams: ActionFactoryParams<CalendarState, CalendarContext>,
): NavigateToNextPage {
  return async ({ selectedSlots, isWaitingListFlow }) => {
    const { getControllerState, context } = actionFactoryParams;
    const [state, setState] = getControllerState();
    const { biLogger, bookingsSettings, flowAPI, preset, hasReferralParam } =
      context;
    const {
      servicesInView,
      selectedTime,
      rescheduleBookingDetails,
      selectedBookingPreferences,
    } = state;

    const selectedSlot: TimeSlot = selectedSlots[0];
    const selectedStaffBookingPreference = selectedBookingPreferences?.find(
      (bookingPreference) => bookingPreference.key === Preference.STAFF_MEMBER,
    );
    if (selectedStaffBookingPreference?.value === DO_NOT_CARE_STAFF) {
      delete selectedSlot.availableResources;
    }
    const selectedServicesIds =
      selectedSlot.nestedTimeSlots!.length > 1
        ? selectedSlot.nestedTimeSlots!.map((slot) => slot.serviceId!)
        : [selectedSlot.serviceId!];
    const selectedServices = servicesInView.filter((service) =>
      selectedServicesIds?.includes(service.id!),
    )!;

    const isTemplate = isTemplateMode({
      wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
      appParams: flowAPI.controllerConfig.appParams,
    });

    const isBookingEnabled = isBookingsEnabled({
      servicesTypes: selectedServices.map((service) => service.type!),
      activeFeatures: bookingsSettings!.features as Features,
      isTemplateMode: isTemplate,
    });

    const isNoBookFlow = isSlotNoBookFlow(selectedSlot);
    const isReschedulingFlow = !!rescheduleBookingDetails;

    const isRegularBookFlow =
      !isNoBookFlow && !isReschedulingFlow && !isWaitingListFlow;

    const shouldReportInitialIntent =
      !isCalendarPage(preset) || !hasReferralParam;

    if (isRegularBookFlow) {
      if (shouldReportInitialIntent) {
        void biLogger.report(
          bookingsUoUFlowBookInitialIntentSrc16Evid33({
            widget_name: PresetToWidgetNameMap[preset],
            serviceType: selectedServices[0].type!,
            bookingsServiceId: selectedServices[0].id!,
            isEligibleForFlowCompletion: isBookingEnabled,
          }),
        );
      }
      void biLogger.report(
        bookingsUoUFlowSlotSelectionCompletedSrc16Evid34({
          widget_name: PresetToWidgetNameMap[preset],
          serviceType: selectedServices[0].type!,
          bookingsServiceId: selectedServices[0].id!,
          startTime: new Date(selectedSlots[0].localStartDate!).getTime(),
          endTime: new Date(selectedSlots[0].localEndDate!).getTime(),
          isEligibleForFlowCompletion: isBookingEnabled,
        }),
      );
    }

    if (isBookingEnabled) {
      const isBookingFormInstalled = await isBookingFormInstalledUtils(
        flowAPI.controllerConfig.wixCodeApi,
      );

      void biLogger.report(
        bookingsContactInfoSaveSuccess({
          selectedSlot: selectedTime,
          ...mapFlowTypeToBiProperties({
            isWaitingList: isWaitingListFlow,
            isBookingFormInstalled,
            isNoBookFlow,
            properties: mapStateToProperties({ state, context }),
          }),
        }),
      );

      if (isNoBookFlow) {
        handleNoBookFlow({
          selectedService: selectedServices[0],
          context,
        });
      } else if (isReschedulingFlow) {
        handleReschedulingFlow(actionFactoryParams);
      } else if (isWaitingListFlow) {
        handleWaitingListFlow(actionFactoryParams);
      } else {
        await handleRegularBookFlow(
          actionFactoryParams,
          selectedSlot,
          selectedServices,
        );
      }
    } else {
      if (flowAPI.environment.isPreview && !isBookingEnabled) {
        setState({
          dialog: {
            type: DialogType.PremiumPreview,
            state: DialogState.IDLE,
          },
        });
      }

      if (flowAPI.environment.isViewer) {
        setState({
          dialog: {
            type: DialogType.PremiumViewer,
            state: DialogState.IDLE,
          },
        });
      }
    }
  };
}

const handleWaitingListFlow = ({
  getControllerState,
}: ActionFactoryParams<CalendarState, CalendarContext>) => {
  const [, setState] = getControllerState();

  setState({
    dialog: {
      type: DialogType.JoinWaitlist,
      state: DialogState.IDLE,
    },
  });
};

const handleReschedulingFlow = ({
  getControllerState,
}: ActionFactoryParams<CalendarState, CalendarContext>) => {
  const [, setState] = getControllerState();

  setState({
    dialog: {
      type: DialogType.RescheduleConfirm,
      state: DialogState.IDLE,
    },
  });
};

const handleNoBookFlow = ({
  selectedService,
  context,
}: {
  selectedService: Service;
  context: CalendarContext;
}) => {
  const {
    preset: referral,
    flowAPI: {
      controllerConfig: { wixCodeApi },
    },
  } = context;
  const serviceSlug = selectedService!.mainSlug?.name!;

  navigateToBookingsServicePage(wixCodeApi, {
    serviceSlug,
    referral,
  });
};

const handleRegularBookFlow = async (
  {
    getControllerState,
    context,
  }: ActionFactoryParams<CalendarState, CalendarContext>,
  slotAvailability: TimeSlot,
  selectedServices: Service[],
) => {
  const {
    bookingsSettings,
    preset,
    isCartEnabled,
    calendarSelections,
    flowAPI: {
      controllerConfig: { wixCodeApi },
      panoramaClient,
    },
    subscriptions: { onNextClicked },
  } = context;
  panoramaClient
    ?.transaction(PanoramaTransactions.CalendarRegularBookFlow)
    .start();
  const [state, setState] = getControllerState();
  const {
    selectedTimezone,
    selectedVariantsOptions,
    serviceVariantsMap,
    selectedBookingPreferences,
  } = state;

  try {
    const isMultiServiceAppointment = slotAvailability.nestedTimeSlots?.length;

    const serviceSlug = selectedServices[0]!.mainSlug?.name!;
    const continueShoppingUrl =
      isCartEnabled && !isMultiServiceAppointment
        ? `/${getCurrentPath(wixCodeApi).join('/')}?${
            BookingsQueryParams.TIMEZONE
          }=${state.selectedTimezone}`
        : undefined;

    const trackingInfo = getTrackingInfoForCalendarNextButtonClick({
      services: selectedServices,
      businessName: bookingsSettings.siteProperties?.businessName || '',
    });

    trackAnalytics({
      wixCodeApi,
      eventId: trackingInfo.eventId as any,
      payload: trackingInfo.payload,
    });

    const addonsInfo = mapCalendarSelectionsToMultiAddonsInfo(
      calendarSelections,
      slotAvailability,
    );
    const isMultipleServices =
      calendarSelections?.services?.length &&
      calendarSelections.services.length > 1;
    const servicesSelections = () => {
      const singleServiceSelection = [
        {
          id: selectedServices[0].id!,
          variants: selectedVariantsOptions,
          resources: [getStaffList(slotAvailability)[0]?.id!],
        },
      ];
      return isMultipleServices
        ? calendarSelections.services
        : singleServiceSelection;
    };

    const dynamicPriceInfo = getDynamicPricingInfo({
      serviceVariantsMap,
      selectedSlot: slotAvailability,
      servicesSelections: servicesSelections(),
    });
    const selectedStaff = selectedBookingPreferences?.find(
      (preference) => preference.key === Preference.STAFF_MEMBER,
    );
    const bookingsSettingsStrategy = bookingsSettings.staffSelection?.strategy;
    let resourceSelectionStrategy: ResourceSelectionStrategy | undefined;
    switch (bookingsSettingsStrategy) {
      case Strategy.CUSTOMER_MUST_CHOOSE:
        resourceSelectionStrategy = ResourceSelectionStrategy.SPECIFIC_RESOURCE;
        break;
      case Strategy.CUSTOMER_MAY_CHOOSE:
        resourceSelectionStrategy =
          selectedStaff && selectedStaff?.value !== DO_NOT_CARE_STAFF
            ? ResourceSelectionStrategy.SPECIFIC_RESOURCE
            : ResourceSelectionStrategy.ANY_RESOURCE;
        break;
      case Strategy.CUSTOMER_CANNOT_CHOOSE:
        resourceSelectionStrategy = ResourceSelectionStrategy.NO_SELECTION;
        break;
      case Strategy.UNKNOWN_STRATEGY:
        resourceSelectionStrategy =
          ResourceSelectionStrategy.UNKNOWN_SELECTION_METHOD;
        break;
      default:
        resourceSelectionStrategy = undefined;
        break;
    }
    setState(
      { calendarStatus: CalendarStatus.NAVIGATING },
      { immediate: true },
    );

    const shouldNavigate =
      !onNextClicked ||
      (await onNextClicked({ service: selectedServices[0], slotAvailability }))
        ?.shouldNavigate;

    panoramaClient
      ?.transaction(PanoramaTransactions.CalendarRegularBookFlow)
      .finish();
    if (shouldNavigate) {
      wixCodeApi.location.queryParams.remove([
        BookingsQueryParams.SKIP_PREFERENCES_MODAL,
      ]);
      void navigateToBookingsFormPage({
        wixCodeApi,
        platformAPIs: context.flowAPI.controllerConfig.platformAPIs,
        timeSlot: slotAvailability,
        serviceSlug,
        dynamicPriceInfo,
        addonsInfo,
        resourceSelectionStrategy,
        queryParams: {
          [BookingsQueryParams.REFERRAL]: preset,
          [BookingsQueryParams.TIMEZONE]: selectedTimezone!,
          ...(isCartEnabled
            ? {
                [BookingsQueryParams.CONTINUE_SHOPPING_URL]:
                  continueShoppingUrl,
              }
            : {}),
        },
        ...(selectedStaff
          ? {
              timeSlotPreferences: {
                selectedResources: [
                  {
                    selectedResourceId: selectedStaff?.value!,
                    resourceTypeId: STAFF_RESOURCE_ID,
                  },
                ],
              },
            }
          : {}),
      });
    }
  } catch (e) {
    setState({ calendarStatus: CalendarStatus.IDLE }, { immediate: true });
  }
};

const mapCalendarSelectionsToMultiAddonsInfo = (
  calendarSelections: CalendarSelections | undefined,
  selectedSlot: TimeSlot,
): MultiAddonsInfo[] | undefined => {
  const { nestedTimeSlots, localStartDate } = selectedSlot;

  const addOnsInfo = calendarSelections?.services
    .map((selections, index) =>
      selections.addOns?.length
        ? {
            serviceId: selections.id,
            startDate: nestedTimeSlots?.length
              ? nestedTimeSlots[index].localStartDate!
              : localStartDate!,
            addonsInfo: mapSelectedAddOnsToAddonsInfo(selections.addOns!),
          }
        : undefined,
    )
    .filter(Boolean) as MultiAddonsInfo[];

  return addOnsInfo?.length ? addOnsInfo : undefined;
};

const mapSelectedAddOnsToAddonsInfo = (
  addons: SelectedAddOn[],
): AddonsInfo[] => {
  return Object.values(
    addons.reduce((groups, addon) => {
      if (!groups[addon.groupId]) {
        groups[addon.groupId] = {
          groupId: addon.groupId,
          addons: [],
        };
      }
      groups[addon.groupId].addons.push({
        id: addon.addOnId,
        quantity: addon.quantity,
      });
      return groups;
    }, {} as Record<string, AddonsInfo>),
  );
};
